// Pure logica van de kalenderweergave in Projecttaken (/mijn-taken?weergave=kalender).
//
// Een taak staat op de kalender van start t/m eind (meerdaags = één balk); zonder start op
// zijn einddatum, en zonder beide op zijn deadline. Het raster loopt ma–zo, met het weekend
// smal (wij werken niet op zaterdag). Per week krijgen balken een "baan" (rij), zoals
// meerdaagse items in een agenda: een balk die over de week loopt schuift de rest naar
// beneden, en overlappende balken komen nooit op dezelfde baan.
import {
  addDays, formatISO, lokaleDatum, schuifWerkdagen, verschuifNaar, werkdagVerschil,
} from '../PlannerTodo/plannerDatums';

/** Maandag van de week waarin `iso` valt. */
export function maandagVan(iso) {
  const d = lokaleDatum(iso);
  if (!d) return null;
  const dag = d.getDay(); // 0 = zo
  return formatISO(addDays(d, dag === 0 ? -6 : 1 - dag));
}

/** Zeven ISO-dagen (ma t/m zo) vanaf een maandag. */
export function weekDagen(maandagISO) {
  const m = lokaleDatum(maandagISO);
  return Array.from({ length: 7 }, (_, i) => formatISO(addDays(m, i)));
}

/**
 * De weken in beeld. `maand`: alle weken die de maand van `anker` raken; `2w`: de week van
 * `anker` en de week erna.
 */
export function wekenInBeeld(ankerISO, bereik = 'maand') {
  if (bereik === '2w') {
    const ma = maandagVan(ankerISO);
    return [weekDagen(ma), weekDagen(formatISO(addDays(lokaleDatum(ma), 7)))];
  }
  const a = lokaleDatum(ankerISO);
  const eerste = formatISO(new Date(a.getFullYear(), a.getMonth(), 1));
  const laatste = formatISO(new Date(a.getFullYear(), a.getMonth() + 1, 0));
  const weken = [];
  for (let ma = maandagVan(eerste); ma <= laatste; ma = formatISO(addDays(lokaleDatum(ma), 7))) {
    weken.push(weekDagen(ma));
  }
  return weken;
}

/** Anker één stap verder of terug: een maand, of twee weken. */
export function verschuifAnker(ankerISO, bereik, richting) {
  const a = lokaleDatum(ankerISO);
  if (bereik === '2w') return formatISO(addDays(a, 14 * richting));
  return formatISO(new Date(a.getFullYear(), a.getMonth() + richting, 1));
}

/** Periode van een taak op de kalender: `{ van, tot }` of null (geen enkele datum). */
export function periodeVan(t) {
  if (!t) return null;
  if (t.start) {
    const tot = t.eind && t.eind >= t.start ? t.eind : t.start;
    return { van: t.start, tot };
  }
  const dag = t.eind || t.deadline;
  return dag ? { van: dag, tot: dag } : null;
}

/**
 * Plaatst taken in één week: `{ items: [{ taak, van, tot, kolVan, kolTot, baan, doorLinks, doorRechts }], banen }`
 * (`van`/`tot` = de dagen in déze week).
 * Kolommen 0–6 = ma–zo. Eerst de vroegste start, bij gelijke start de langste balk (die
 * leest het rustigst bovenaan), dan op naam — zodat de volgorde stabiel is.
 */
export function plaatsInWeek(taken, week) {
  const [ma, zo] = [week[0], week[6]];
  const kandidaten = (taken || [])
    .map((taak) => ({ taak, p: periodeVan(taak) }))
    .filter(({ p }) => p && p.van <= zo && p.tot >= ma)
    .map(({ taak, p }) => {
      const van = p.van < ma ? ma : p.van;
      const tot = p.tot > zo ? zo : p.tot;
      return {
        taak, van, tot, kolVan: week.indexOf(van), kolTot: week.indexOf(tot),
        doorLinks: p.van < ma, doorRechts: p.tot > zo,
      };
    })
    .sort((a, b) => a.kolVan - b.kolVan
      || (b.kolTot - b.kolVan) - (a.kolTot - a.kolVan)
      || String(a.taak.naam || '').localeCompare(String(b.taak.naam || ''), 'nl'));

  const banen = []; // per baan de laatst bezette kolom
  const items = kandidaten.map((it) => {
    let baan = banen.findIndex((laatste) => laatste < it.kolVan);
    if (baan < 0) { baan = banen.length; banen.push(it.kolTot); } else banen[baan] = it.kolTot;
    return { ...it, baan };
  });
  return { items, banen: banen.length };
}

/**
 * Wat past er zichtbaar in een week met maximaal `max` banen? Items op hogere banen
 * verdwijnen, en per dag telt `verborgen[kol]` hoeveel er dan niet te zien zijn ("+2 meer").
 * Met precies één baan te veel tonen we die gewoon: "+1 meer" kost evenveel ruimte.
 */
export function beperkBanen({ items, banen }, max) {
  if (!max || banen <= max + 1) return { zichtbaar: items, verborgen: Array(7).fill(0), banen };
  const zichtbaar = items.filter((it) => it.baan < max);
  const verborgen = Array(7).fill(0);
  items.filter((it) => it.baan >= max).forEach((it) => {
    for (let k = it.kolVan; k <= it.kolTot; k += 1) verborgen[k] += 1;
  });
  return { zichtbaar, verborgen, banen: max };
}

/** Is `iso` een weekenddag? */
export const isWeekend = (iso) => {
  const d = lokaleDatum(iso);
  return Boolean(d) && (d.getDay() === 0 || d.getDay() === 6);
};

/**
 * Een taak naar een andere dag slepen. `gepaktOp` is de dag van de balk waar je hem vastpakte
 * (bij een meerdaagse balk is dat niet altijd de start); de balk schuift evenveel werkdagen
 * als je hem verplaatst, en de looptijd in werkdagen blijft gelijk (verschuifNaar).
 * Zonder start (alleen eind of deadline) wordt de taak op die dag ingepland.
 * Geeft de PATCH-velden, of null als er niets verandert.
 */
export function sleepVelden(t, gepaktOp, doelISO) {
  const p = periodeVan(t);
  if (!p || !doelISO) return null;
  const vast = gepaktOp && gepaktOp >= p.van && gepaktOp <= p.tot ? gepaktOp : p.van;
  if (vast === doelISO) return null;
  if (!t.start) {
    return { StartDateUitvoerder: doelISO, EndDateUitvoerder: doelISO };
  }
  const n = werkdagVerschil(vast, doelISO);
  // Oppakken op dag 3 van een balk en loslaten op dinsdag = de start schuift n werkdagen.
  const nieuweStart = vast === p.van ? doelISO : schuifWerkdagen(t.start, n);
  const { start, eind } = verschuifNaar(t.start, t.eind || t.start, nieuweStart);
  if (start === t.start && eind === (t.eind || t.start)) return null;
  return { StartDateUitvoerder: start, EndDateUitvoerder: eind };
}

// Eén gedempte tint per project: grijsblauwen, geen 16-kleurenlijst.
export const PROJECT_TINTEN = ['#94a3b8', '#8aa0b8', '#a3a8b8', '#8fa9a6', '#a8a095'];

/** Vaste tint per project (hash op id of naam); zonder project de lichtste grijs. */
export function tintVoor(projectSleutel) {
  const s = String(projectSleutel || '');
  if (!s) return '#d1d5db';
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0; // eslint-disable-line no-bitwise
  return PROJECT_TINTEN[h % PROJECT_TINTEN.length];
}

/** Kop boven het raster: "oktober 2026" of "28 sep – 11 okt". */
export function bereikLabel(weken, ankerISO, bereik) {
  if (bereik === '2w') {
    const fmt = (iso) => lokaleDatum(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
    return `${fmt(weken[0][0])} – ${fmt(weken[weken.length - 1][4])}`;
  }
  return lokaleDatum(ankerISO).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
}
